import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import cn from 'classnames';
import { connect } from 'react-redux';

import { I18nextProvider } from 'react-i18next';
import i18n from '../../i18n';

import '../../containers/App.scss';
import Footer from '../Footer';
import Navigation from '../Navigation/Navigation';
import Alert from '../Alert';
import { graphql } from 'gatsby';
import _ from 'lodash';
import { Helmet } from 'react-helmet';
import favicon from '../../assets/favicon.ico';
import { ScrollContext, ScrollContextProvider } from '../../util/ScrollContext';

const LayoutWrapper = ({ children, showAlert, language }) => {
  const state = useContext(ScrollContext)

  const className = cn('App', {
    'has-alert': showAlert,
    'no-scroll': state.menuOpened,
  });

  return (
    <div id="App" className={className}>
      <Helmet>
        <html lang={language.toLowerCase()} />
        <link rel="icon" href={`${process.env.GATSBY_BASE_URL}/favicon.svg`} type="image/svg+xml" />
        <link rel="alternate icon" href={favicon} type="image/vnd.microsoft.icon" />
      </Helmet>
      {children}
    </div>
  )
}

const Layout = ({ home, language, urls, children, hideAlert, setAlert, alert }) => {
  const closeAlert = () => {
    setAlert(true);
  };

  const showAlert = alert && !hideAlert && (alert.fieldAllPages || home)

  i18n.changeLanguage(language.toLowerCase());

  return (
    <I18nextProvider i18n={i18n}>
      <ScrollContextProvider>
        <LayoutWrapper showAlert={showAlert} language={language}>
          <Navigation language={language} urls={urls} />
          <CSSTransition in={showAlert} unmountOnExit timeout={3000} classNames="alert">
            <Alert
              showAlert={showAlert}
              title={_.get(alert, 'title', '')}
              description={_.get(alert, 'fieldIntroText', '')}
              url={_.get(alert, 'fieldDetailLink.url.path', null)}
              dismissable={_.get(alert, 'fieldDismissable', true)}
              onClose={() => closeAlert()}
              />
            </CSSTransition>
            {children}
          <Footer language={language} urls={urls}/>
        </LayoutWrapper>
      </ScrollContextProvider>
    </I18nextProvider>
  );
}

LayoutWrapper.propTypes = {
  home: PropTypes.bool,
  language: PropTypes.string,
  urls: PropTypes.object
}

LayoutWrapper.defaultProps = {
  home: false,
  language: "NL",
  urls: {
    "nl": "/",
    "en": "/en/"
  }
}

const mapStateToProps = ({ hideAlert }) => {
  return { hideAlert }
}

const mapDispatchToProps = dispatch => {
  return { setAlert: () => dispatch({ type: `HIDE_ALERT` }) }
}

export default connect(mapStateToProps, mapDispatchToProps)(Layout);

// Shared fragments
export const query = graphql`
  fragment Image on CMS_MediaImage {
    fieldMediaImage {
      entity {
        uri {
          url
        }
      }
      alt
    }
  }

  fragment CardImage on CMS_MediaImage {
    fieldMediaImage {
      cardImage: derivative (style: CARD) {
        ... on CMS_ImageResource {
          height
          width
          url
          image {
            childImageSharp {
              fluid(maxWidth: 1000) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
      }
      alt
    }
  }

  fragment LinkTileImage on CMS_MediaImage {
    fieldMediaImage {
      linkTileImage: derivative (style: LINKTILE) {
        ... on CMS_ImageResource {
          height
          width
          url
          image {
            childImageSharp {
              fluid(maxWidth: 1000) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
      }
      alt
    }
  }

  fragment HeaderImage on CMS_MediaImage {
    fieldMediaImage {
      headerImage: derivative (style: HEADER) {
        ... on CMS_ImageResource {
          height
          width
          url
          image {
            childImageSharp {
              fluid(maxWidth: 3000) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
      }
      alt
    }
  }

  fragment Container on CMS_ParagraphContainer {
    fieldItems {
      entity {
        entityId
        entityBundle
        ...Card
        ...CardMerger
        ...LinkList
        ...EmbedBlock
        ...ContactBlocks
        ...Checklist
        ...LargeLinkTile
        ...AppBlock
        ...Rijksvideo
      }
    }
    fieldLayout
  }

  fragment LinkList on CMS_ParagraphLinkList {
    fieldTitle
    fieldLinks {
      title
      url {
        path
      }
    }
    fieldClassName
  }

  fragment Checklist on CMS_ParagraphChecklist {
    fieldTitle
    fieldItem
  }

  fragment LargeLinkTile on CMS_ParagraphLargeLinkTile {
    fieldTitle
    fieldText
    fieldBackground {
      entity {
        ... LinkTileImage
      }
    }
    fieldMoreLink {
      title
      url {
        path
      }
    }
    fieldMoreLinkLabel
  }

  fragment EmbedBlock on CMS_ParagraphEmbedBlock {
    fieldTitle
    fieldBlockName
    fieldClassName
  }

  fragment CardMerger on CMS_ParagraphCardMerger {
    fieldItems {
      entity {
        entityId
        entityBundle
        ...Card
      }
    }
  }

  fragment Card on CMS_ParagraphCard {
    fieldIcon {
      entity {
        ...Icon
      }
    }
    fieldBackground {
      entity {
        ... CardImage
      }
    }
    fieldText
    fieldTitle
    fieldTitleTag
    fieldAltText
    fieldCardType
    fieldMoreLinkType
    fieldMoreLinkLabel
    fieldMoreLink {
      title
      url {
        path
      }
    }
    fieldItems {
      entity {
        entityId
        entityBundle
        ...InfoBlocks
        ...AppButtons
      }
    }
  }

  fragment AppButtons on CMS_ParagraphAppButtons {
    fieldAppstoreUrl {
      url {
        path
      }
    }
    fieldPlaystoreUrl {
      url {
        path
      }
    }
  }

  fragment AppBlock on CMS_ParagraphAppBlock {
    fieldTitle
    fieldSmallText
    fieldBackground {
      entity {
        ... CardImage
      }
    }
    fieldAppstoreUrl {
      url {
        path
      }
    }
    fieldPlaystoreUrl {
      url {
        path
      }
    }
  }

  fragment Icon on CMS_MediaIcon {
    fieldMediaIcon {
      entity {
        ... on CMS_File {
          uri {
            url
          }
        }
      }
    }
  }

  fragment InfoBlock on CMS_ParagraphInfoBlock {
    fieldIcon {
      entity {
        ... Icon
      }
    }
    fieldClassName
    fieldTitle
    fieldText
    fieldMoreLink {
      title
      url {
        path
      }
    }
    fieldMoreLinkLabel
  }

  fragment InfoBlocks on CMS_ParagraphInfoBlocks {
    fieldItems {
      entity {
        ... InfoBlock
      }
    }
    fieldInfoHalfSize
    fieldTitle
    fieldText
    fieldSmallText
  }

  fragment LinkBlocks on CMS_ParagraphLinkBlocks {
    fieldItems {
      entity {
        entityId
        entityBundle
        ... Wysiwyg
      }
    }
  }

  fragment ContactBlocks on CMS_ParagraphContactBlocks {
    fieldItems {
      entity {
        entityId
        entityBundle
        ... ContactBlock
      }
    }
  }

  fragment ContactBlock on CMS_ParagraphContactBlock {
    fieldIcon {
      entity {
        ... Icon
      }
    }
    fieldTitle
    fieldBody {
      processed
    }
    fieldMoreLink {
      title
      uri
      url {
        path
      }
    }
    fieldMoreLinkLabel
  }

  fragment AccordionItem on CMS_ParagraphAccordionItem {
    fieldTitle
    fieldAnchor
    fieldBody {
      processed
    }
  }

  fragment Accordion on CMS_ParagraphContentAccordion {
    fieldTitle,
    fieldItems {
      entity {
        entityId
        ...AccordionItem
      }
    }
  }

  fragment Wysiwyg on CMS_ParagraphContentWysiwyg {
    fieldTitle
    fieldBody {
      processed
    }
    fieldMoreLink {
      title
      url {
        path
      }
    }
    fieldMoreLinkLabel
  }

  fragment ReusableBlock on CMS_ParagraphReusableBlock {
    fieldReusableBlock {
      entity {
        ... on CMS_TaxonomyTermReusableBlocks {
          fieldContent {
            entity {
              ...Wysiwyg
              ...Accordion
            }
          }
        }
      }
    }
  }

  fragment Rijksvideo on CMS_ParagraphVideo {
    fieldTitle
    fieldRijksvideo {
      entity {
        ... on CMS_NodeRijksvideo {
          fieldRijksvideoAudio
          fieldRijksvideoBronnen
          fieldRijksvideoDuratie
          fieldRijksvideoThumbnail {
            ... on CMS_FieldNodeRijksvideoFieldRijksvideoThumbnail {
              entity {
                uri {
                  url
                }
              }
            }
          }
          fieldRijksvideoTranscriptie
          fieldRijksvideoOndertiteling {
            entity {
              uri {
                url
              }
            }
          }
        }
      }
    }
  }

  fragment Link on CMS_MenuLink {
    label
    url {
      path
    }
  }

`

